// Central date formatter — every user-visible date in LIM Flow uses dd/mm/yy.
// Times use 24-hour HH:mm to match Indian corporate defaults.

const pad = (n) => String(n).padStart(2, "0");

/** Return dd/mm/yy for any Date or parseable input. Returns "—" on invalid. */
export function fmtDate(input) {
  if (!input) return "—";
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return "—";
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)}`;
}

/** dd/mm/yy · HH:mm — used when both date + time matter (deadlines, chat, history). */
export function fmtDateTime(input) {
  if (!input) return "—";
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return "—";
  return `${fmtDate(d)} · ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** HH:mm only — for notifications that arrived "today". */
export function fmtTime(input) {
  if (!input) return "";
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return "";
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** YYYY-MM-DD (ISO). Keep server-facing dates unchanged. */
export function isoDate(input = new Date()) {
  const d = input instanceof Date ? input : new Date(input);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** YYYY-MM-DD in Asia/Kolkata (IST). LIM Flow is India-only, so every
 * "today" that hits the API or drives the log picker must be IST-anchored —
 * otherwise UTC drift (or a user on a foreign VPN) files logs under the wrong day.
 */
export function todayIstIso(now = new Date()) {
  // en-CA locale renders as YYYY-MM-DD, which is exactly what we send to the API.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** dd/mm/yy for the current instant, rendered in IST. Header/status bars only —
 * use fmtDate(isoString) for stored dates. */
export function todayIstDmy(now = new Date()) {
  const iso = todayIstIso(now); // YYYY-MM-DD in IST
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y.slice(-2)}`;
}

/** True if the ISO/date is a Saturday or Sunday in the local calendar. */
export function isWeekend(input) {
  if (!input) return false;
  const d = _parseLocal(input);
  if (!d) return false;
  const day = d.getDay();
  return day === 0 || day === 6;
}

/** Full weekday name ("Saturday"). Empty on invalid. */
export function weekdayName(input) {
  if (!input) return "";
  const d = _parseLocal(input);
  if (!d) return "";
  return d.toLocaleDateString(undefined, { weekday: "long" });
}

/** Parse "YYYY-MM-DD" as a local date to avoid UTC-shift bugs at day boundaries. */
function _parseLocal(input) {
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const s = String(input);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

